/**
 * Cashback copy selectors. They only choose WHICH sentence to show and feed it
 * the numbers the server already computed: no reward is ever calculated here.
 * Keys live under `cashback.*` in the shop locale files; params use {{name}}.
 */

export type ActiveCashback = {
  active: true;
  promotionId: string;
  currency: string;
  rateBp: number;
  minimumCents: number;
  capCents: number | null;
  rewardAtMinimumCents: number;
  endsAt: string;
  lotLifetimeDays: number;
};

/** GET /cashback/active */
export type ActiveCashbackResponse = { active: false } | ActiveCashback;

/** The coupon field's figures (backend OrderCashbackCouponView). */
export type OrderCashbackCouponView = {
  code: string;
  rewardCents: number;
  minimumCents: number;
  missingCents: number;
  /** True when the coupon is the cashback this order gets. */
  wins: boolean;
};

/** GET /order/:id/cashback (OrderCashbackView in the backend). */
export type OrderCashbackView = {
  state:
    | "none"
    | "preview"
    | "pending"
    | "granted"
    | "reduced"
    | "canceled"
    | "voided";
  currency: string | null;
  qualifyingCents: number;
  rewardCents: number;
  minimumCents: number;
  missingCents: number;
  rewardAtMinimumCents: number;
  capped: boolean;
  email: string | null;
  codPendingDelivery: boolean;
  grantedAt: string | null;
  expiresAt: string | null;
  remainingCents: number | null;
  /** Whose figures the fields above are: the winner, else the promotion, else the coupon. */
  source?: "promotion" | "coupon" | null;
  /** The order's usable cashback coupon, for the coupon field. Older servers omit it. */
  coupon?: OrderCashbackCouponView | null;
};

export type CopyLine = { key: string; params: Record<string, string> };

type Fmt = (cents: number) => string;

const POLL_INTERVAL_MS = 5_000;
const POLL_GIVE_UP_MS = 180_000;

export function ratePercentLabel(rateBp: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(
    rateBp / 100,
  );
}

export function needsGuestHowTo(
  view: OrderCashbackView,
  accountEmail: string | null | undefined,
): boolean {
  return (
    !!view.email &&
    (!accountEmail ||
      accountEmail.trim().toLowerCase() !== view.email.toLowerCase())
  );
}

const howTo = (view: OrderCashbackView): CopyLine => ({
  key: "cashback.guestHowToUse",
  params: { email: view.email ?? "" },
});

export function bannerCopy(
  promo: ActiveCashback,
  fmt: Fmt,
  locale: string,
  endsAtLabel: string,
): CopyLine[] {
  const rate = ratePercentLabel(promo.rateBp, locale);
  const headline: CopyLine =
    promo.minimumCents > 0
      ? {
          key: "cashback.bannerHeadline",
          params: {
            minimum: fmt(promo.minimumCents),
            reward: fmt(promo.rewardAtMinimumCents),
          },
        }
      : { key: "cashback.bannerHeadlineNoMinimum", params: { rate } };
  const detail: CopyLine =
    promo.capCents != null
      ? {
          key: "cashback.bannerDetailCapped",
          params: { rate, end: endsAtLabel, cap: fmt(promo.capCents) },
        }
      : { key: "cashback.bannerDetail", params: { rate, end: endsAtLabel } };
  return [headline, detail];
}

/**
 * Whether credit the customer holds may still go on this cart at the
 * delivery step, and so lower the cashback the cart drawer previews.
 *
 * Credit is applied only on the delivery step (`creditControl`), and the
 * reward is earned on what is paid, so the drawer's preview is a ceiling for
 * a customer with credit: 2,10 € in the cart became 1,89 € at checkout
 * (guest cashback walk, B7). Re-computing the reward net of credit here was
 * rejected: it would copy the server's rate, cap and minimum logic and guess
 * which lots auto-apply. The figure stays the server's; only its wording
 * changes.
 *
 *  - a claim token held while the cart has no email yet: the token may be
 *    sent only for the order's email, so it is not a credential YET, but the
 *    delivery step applies it once the email is in. Unknown, so it may
 *    (final walk NEW-2);
 *  - no credential (a guest without a claim link): no credit can apply;
 *  - credential, balance still loading: it may;
 *  - the balance read failed or was refused: the delivery step only
 *    auto-applies a KNOWN balance (`creditControl`), so nothing will;
 *  - known balance: only if something is left to spend. Credit already on
 *    the order is inside the server's preview already.
 */
export function creditMayLowerCashback(input: {
  credentialHeld: boolean;
  /** Still spendable after anything on this order; null when not known. */
  balanceCents: number | null;
  balanceFailed?: boolean;
  /** A live claim token is stored, and the order has no email to match it to yet. */
  tokenAwaitingEmail?: boolean;
}): boolean {
  if (!input.credentialHeld) return !!input.tokenAwaitingEmail;
  if (input.balanceFailed) return false;
  if (input.balanceCents === null) return true;
  return input.balanceCents > 0;
}

export function progressCopy(
  view: OrderCashbackView,
  fmt: Fmt,
  opts: { creditMayApply?: boolean } = {},
): CopyLine | null {
  if (view.state !== "preview" || view.qualifyingCents <= 0) return null;
  if (view.missingCents > 0) {
    return {
      key: "cashback.progressMissing",
      params: {
        missing: fmt(view.missingCents),
        reward: fmt(view.rewardAtMinimumCents),
      },
    };
  }
  if (opts.creditMayApply) {
    return {
      key: "cashback.progressReachedUpTo",
      params: { reward: fmt(view.rewardCents) },
    };
  }
  return {
    key: view.capped
      ? "cashback.progressReachedCapped"
      : "cashback.progressReached",
    params: { reward: fmt(view.rewardCents) },
  };
}

export function checkoutCopy(
  view: OrderCashbackView,
  fmt: Fmt,
  ctx: { accountEmail: string | null },
): CopyLine[] {
  if (view.rewardCents <= 0) return [];
  const lines: CopyLine[] = [
    {
      key: view.codPendingDelivery
        ? "cashback.checkoutCod"
        : "cashback.checkoutCard",
      params: { reward: fmt(view.rewardCents) },
    },
  ];
  if (needsGuestHowTo(view, ctx.accountEmail)) lines.push(howTo(view));
  return lines;
}

export function summaryCopy(
  view: OrderCashbackView,
  fmt: Fmt,
  ctx: { accountEmail: string | null; expiresLabel: string },
): CopyLine[] {
  const reward = fmt(view.rewardCents);
  const email = view.email ?? "";
  let first: CopyLine;
  switch (view.state) {
    case "granted":
    case "reduced":
      first = { key: "cashback.summaryGranted", params: { reward, email } };
      break;
    case "pending":
    case "preview":
      first = {
        key: view.codPendingDelivery
          ? "cashback.summaryPendingCod"
          : "cashback.summaryPendingCard",
        params: { reward, email },
      };
      break;
    default:
      return [];
  }
  const lines = [first];
  if (needsGuestHowTo(view, ctx.accountEmail)) lines.push(howTo(view));
  if (
    (view.state === "granted" || view.state === "reduced") &&
    view.expiresAt
  ) {
    lines.push({
      key: "cashback.summaryExpires",
      params: { date: ctx.expiresLabel },
    });
  }
  return lines;
}

export function detailCopy(
  view: OrderCashbackView,
  fmt: Fmt,
  labels: { date: string; expires: string },
): CopyLine | null {
  const reward = fmt(view.rewardCents);
  switch (view.state) {
    case "pending":
      return {
        key: view.codPendingDelivery
          ? "cashback.detailPendingCod"
          : "cashback.detailPendingCard",
        params: { reward },
      };
    case "granted":
      return {
        key: "cashback.detailGranted",
        params: { reward, date: labels.date, expires: labels.expires },
      };
    case "reduced":
      return {
        key: "cashback.detailReduced",
        params: { reward, date: labels.date, expires: labels.expires },
      };
    case "voided":
      return { key: "cashback.detailVoided", params: {} };
    default:
      return null;
  }
}

/**
 * Thank-you polling: card orders only, while the grant may still land, for 3
 * minutes at most. A failed request (401, 404, network) stops it: asking the
 * same question every 5 s will not change the answer.
 */
export function cashbackPollMs(
  view: OrderCashbackView | undefined,
  elapsedMs: number,
  failed = false,
): number | false {
  if (failed || elapsedMs > POLL_GIVE_UP_MS) return false;
  if (!view) return POLL_INTERVAL_MS;
  if (
    (view.state === "preview" || view.state === "pending") &&
    !view.codPendingDelivery
  )
    return POLL_INTERVAL_MS;
  return false;
}

/**
 * The line under a cashback coupon's chip. The coupon wins → what the
 * customer receives; below its own minimum → how much is missing; a running
 * promotion gives more → that one, and that the two do not add up.
 *
 * `rewardShownBelow`: the screen also renders the order's cashback note
 * (the delivery step), which states the same reward with when and how it
 * arrives. The winning sentence is then left to the note; the other two stay,
 * because the note does not explain them.
 *
 * `creditMayApply`: the cart drawer, where credit has not been applied yet
 * and may lower the reward at the delivery step (`creditMayLowerCashback`).
 */
export function couponCashbackLine(
  view: OrderCashbackView | null | undefined,
  fmt: Fmt,
  opts: { rewardShownBelow?: boolean; creditMayApply?: boolean } = {},
): CopyLine | null {
  const c = view?.coupon;
  if (!view || !c) return null;
  if (c.wins && c.rewardCents > 0) {
    if (opts.rewardShownBelow) return null;
    return {
      key: opts.creditMayApply ? "cashback.couponRewardUpTo" : "cashback.couponReward",
      params: { reward: fmt(c.rewardCents) },
    };
  }
  if (c.missingCents > 0)
    return {
      key: "cashback.couponBelowMinimum",
      params: { minimum: fmt(c.minimumCents), missing: fmt(c.missingCents) },
    };
  if (view.source === "promotion" && view.rewardCents > 0)
    return { key: "cashback.couponPromotionWins", params: { reward: fmt(view.rewardCents) } };
  return null;
}

/**
 * Whether to ask GET /order/:id/cashback. The switch gates promotions only:
 * a cart whose coupon is a cashback coupon asks without it (spec D1), and a
 * placed order (thank-you, detail) always asks — its coupon may be gone and
 * the server answers `none` cheaply.
 */
export function cashbackQueryEnabled(o: {
  switchOn: boolean;
  orderId: string | null | undefined;
  couponIsCashback?: boolean;
  placed?: boolean;
  enabled?: boolean;
}): boolean {
  if (!o.orderId || o.enabled === false) return false;
  return o.switchOn || o.couponIsCashback === true || o.placed === true;
}


/**
 * The cache key for a cart's cashback preview. A cashback coupon prices at 0,
 * so attaching or removing one leaves `amount` unchanged: the coupon's
 * identity is part of the key, or the previous answer keeps being shown.
 */
export function cashbackAmountKey(
  order:
    | {
        amount?: number | null;
        creditAppliedCents?: number | null;
        couponUsed?: { id?: string | null; code?: string | null } | null;
      }
    | null
    | undefined,
): unknown[] {
  const coupon = order?.couponUsed;
  return [
    order?.amount ?? null,
    order?.creditAppliedCents ?? null,
    coupon?.id ?? coupon?.code ?? null,
  ];
}
